import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Tile colours. `glass` is the translucent tile for navy surfaces (peach icon);
 * the others are for light surfaces.
 */
export const ICON_BADGE_TONES = {
  navy: 'bg-navy text-brand-sheet',
  butter: 'bg-brand-butter text-navy',
  sage: 'bg-brand-sage text-navy',
  lilac: 'bg-brand-lilac text-navy',
  peach: 'bg-brand-peach text-navy',
  sheet: 'border border-brand-rule bg-brand-sheet text-navy',
  glass: 'border border-brand-sheet/20 bg-brand-sheet/10 text-brand-peach',
};

/** sm 39px (step numbers), md 45px (section / pass icons), lg 54 → 60 → 75px (band icons). */
export const ICON_BADGE_SIZES = {
  sm: 'size-[2.4375rem] text-xs [&_svg]:size-[18px]',
  md: 'size-[2.8125rem] text-sm [&_svg]:size-[22px]',
  lg: 'size-[3.375rem] text-base nav:size-[3.75rem] lg:size-[4.6875rem] [&_svg]:size-[26px] lg:[&_svg]:size-[33px]',
};

/**
 * The small icon or number tile the reference puts at the head of cards, steps and
 * coloured bands (round by default, as in the package; `shape="square"` for a square tile).
 * Pass a lucide icon or a short number ("01") as children.
 *
 * Props:
 * - `tone`: 'navy' (default) | 'butter' | 'sage' | 'lilac' | 'peach' | 'sheet' | 'glass'
 * - `size`: 'sm' | 'md' (default) | 'lg'
 * - `shape`: 'circle' (default) | 'square'
 * - `tilt`: true = rotated −9deg (the discovery break icon)
 * - `offset`: true = flat orange offset shadow (the numbered steps)
 * - `label`: accessible name — given, the tile is role="img" with that label;
 *   omitted (default), it is decorative (aria-hidden)
 * - `as`: element (default 'span'); `className`, `children`, other props pass through.
 */
export default function IconBadge({
  tone = 'navy',
  size = 'md',
  shape = 'circle',
  tilt = false,
  offset = false,
  label,
  as: Tag = 'span',
  className,
  children,
  ...rest
}) {
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': 'true' };
  return (
    <Tag
      {...a11y}
      className={cn(
        'inline-grid shrink-0 place-items-center font-label',
        ICON_BADGE_TONES[tone] || ICON_BADGE_TONES.navy,
        ICON_BADGE_SIZES[size] || ICON_BADGE_SIZES.md,
        'leading-none', // after the size: a later text-[size] would drop it in cn()
        shape === 'square' ? 'rounded-none' : 'rounded-full',
        tilt && '-rotate-[9deg]',
        offset && 'shadow-[3px_3px_0] shadow-brand-orange/35',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
